import { apiClient } from '../client';
import type {
  SchedulerAssigneesParams,
  SchedulerAssigneesResponse,
  SchedulerRecentAssigneesParams,
  SchedulerEntriesParams,
  SchedulerEntriesResponse,
  SchedulerOrderEntriesOptions,
  SchedulerOrderEntriesResponse,
} from './types';

export const schedulerEntriesQueryKeys = {
  all: ['schedulerEntries'] as const,
  list: (params: SchedulerEntriesParams) => [...schedulerEntriesQueryKeys.all, params] as const,
  forOrder: (orderId: number) => [...schedulerEntriesQueryKeys.all, 'order', orderId] as const,
  order: (orderId: number, { includeAssignments = false }: SchedulerOrderEntriesOptions<boolean> = {}) =>
    [...schedulerEntriesQueryKeys.forOrder(orderId), { includeAssignments }] as const,
};

export async function getSchedulerEntries(params: SchedulerEntriesParams): Promise<SchedulerEntriesResponse> {
  const response = await apiClient.get<SchedulerEntriesResponse>('/api/scheduler/entries', { params });
  return response.data;
}

export async function getSchedulerOrderEntries<TIncludeAssignments extends boolean = false>(
  orderId: number,
  { includeAssignments }: SchedulerOrderEntriesOptions<TIncludeAssignments> = {},
): Promise<SchedulerOrderEntriesResponse<TIncludeAssignments>> {
  const response = await apiClient.get<SchedulerOrderEntriesResponse<TIncludeAssignments>>(
    `/api/scheduler/orders/${orderId}/entries`,
    { params: includeAssignments ? { include: 'assignments' } : {} },
  );
  return response.data;
}

export const schedulerAssigneesQueryKeys = {
  all: ['schedulerAssignees'] as const,
  day: (date: string) => [...schedulerAssigneesQueryKeys.all, date] as const,
  recent: ({ eventId, job }: SchedulerRecentAssigneesParams) =>
    [...schedulerAssigneesQueryKeys.all, 'recent', eventId, job] as const,
};

export async function getSchedulerAssignees(params: SchedulerAssigneesParams): Promise<SchedulerAssigneesResponse> {
  const response = await apiClient.get<SchedulerAssigneesResponse>('/api/scheduler/assignees', { params });
  return response.data;
}

export async function getSchedulerRecentAssignees({
  eventId,
  job,
}: SchedulerRecentAssigneesParams): Promise<SchedulerAssigneesResponse> {
  const response = await apiClient.get<SchedulerAssigneesResponse>(
    `/api/scheduler/entries/${eventId}/${job}/recent_assignees`,
  );
  return response.data;
}

export type SchedulerSlingExportStatus = 'sending' | 'sent' | 'partly_sent' | 'failed';

export interface SchedulerSlingLastSent {
  status: SchedulerSlingExportStatus;
  at: string;
  by: string | null;
}

export interface SchedulerSlingLastSentResponse {
  sling_export: SchedulerSlingLastSent | null;
  has_people: boolean;
}

export const schedulerSlingExportQueryKeys = {
  all: ['schedulerSlingExport'] as const,
  day: (date: string) => [...schedulerSlingExportQueryKeys.all, date] as const,
};

export async function getSchedulerSlingLastSent(date: string): Promise<SchedulerSlingLastSentResponse> {
  const response = await apiClient.get<SchedulerSlingLastSentResponse>('/api/scheduler/sling_exports', {
    params: { date },
  });
  return response.data;
}
